import React, { useState } from 'react';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import {
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as TaskIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `dashboard-tab-${index}`,
    'aria-controls': `dashboard-tabpanel-${index}`,
  };
}

interface DashboardTabsProps {
  overviewType: string;
  children: React.ReactNode;
}

interface TabContent {
  overview: React.ReactNode;
  performance: React.ReactNode;
  tasks: React.ReactNode;
  hearings?: React.ReactNode;
  billing?: React.ReactNode;
}

export const DashboardTabs: React.FC<DashboardTabsProps> = ({ 
  overviewType, 
  children 
}) => {
  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // Determine which tabs to show based on overview type
  const getTabs = () => {
    const commonTabs = [
      {
        label: 'Practice Overview',
        icon: <BusinessIcon />,
      },
      {
        label: 'Performance Metrics',
        icon: <TrendingUpIcon />,
      },
      {
        label: 'Tasks',
        icon: <TaskIcon />,
      },
    ];

    // Add tabs based on user role
    if (overviewType === 'admin' || overviewType === 'lawyer') {
      commonTabs.push({
        label: 'Tasks & Hearings',
        icon: <CalendarIcon />,
      });
    }

    if (overviewType === 'client') {
      commonTabs.push({
        label: 'Billing',
        icon: <TrendingUpIcon />,
      });
    }

    return commonTabs;
  };

  const tabs = getTabs();
  
  // Extract tab content from children - handle as ReactNode
  const childrenArray = React.Children.toArray(children);
  const tabContent: TabContent = {
    overview: childrenArray[0] || null,
    performance: childrenArray[1] || null,
    tasks: childrenArray[2] || null,
    hearings: childrenArray[3] || null,
    billing: childrenArray[3] || null,
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="dashboard tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tab.icon}
                  <Typography variant="body2" sx={{ textTransform: 'none' }}>
                    {tab.label}
                  </Typography>
                </Box>
              }
              {...a11yProps(index)}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={value} index={0}>
        {tabContent.overview}
      </TabPanel>
      
      <TabPanel value={value} index={1}>
        {tabContent.performance}
      </TabPanel>
      
      <TabPanel value={value} index={2}>
        {tabContent.tasks}
      </TabPanel>

      {(overviewType === 'admin' || overviewType === 'lawyer') && (
        <TabPanel value={value} index={3}>
          {tabContent.hearings}
        </TabPanel>
      )}

      {overviewType === 'client' && (
        <TabPanel value={value} index={3}>
          {tabContent.billing}
        </TabPanel>
      )}
    </Box>
  );
};
